import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useAgoraCall } from './useAgoraCall';
import { useAgoraRtm } from './useAgoraRtm';
import { useQrVisitorContext } from './useQrVisitorContext';
import { getCallSignaling } from '../../api/qr/callSignaling';
import { isOutsideQrIntercomGeofence } from '../../api/qr/qrIntercom';
import {
    CALL_STATUS_POLL_INTERVAL_MS, CALL_RING_TIMEOUT_MS, CALL_RECOVERY_TIMEOUT_MS, CALL_RECOVERY_RETRY_MS,
    CALL_RECOVERY_ATTEMPTS, REMOTE_RECONNECT_GRACE_MS, QR_CALL_ERROR,
    QR_CALL_FINAL_STATUSES, QR_CALL_STAGE, QR_CALL_STATUS, QR_RTM_COMMAND,
    RTM_OPERATION_TIMEOUT_MS,
} from '../../constants/qr';

const STAGE_BY_FINAL_STATUS = {
    [QR_CALL_STATUS.DECLINED]: QR_CALL_STAGE.DECLINED,
    [QR_CALL_STATUS.MISSED]: QR_CALL_STAGE.MISSED,
    [QR_CALL_STATUS.ENDED]: QR_CALL_STAGE.ENDED,
};

const BUSY_STAGES = [QR_CALL_STAGE.CONNECTING, QR_CALL_STAGE.RINGING, QR_CALL_STAGE.IN_CALL];

export function useQrCall({
    entranceId, residentId, getRtmConfig, onOutsideGeofence,
}) {
    const signaling = getCallSignaling();
    const media = useAgoraCall();
    const rtm = useAgoraRtm();
    const visitor = useQrVisitorContext();

    const stage = ref(QR_CALL_STAGE.PRECALL);
    const endedByResident = ref(false);
    const ringTimedOut = ref(false);
    const errorCode = ref(null);
    const lockOpened = ref(false);
    const elapsedSeconds = ref(0);
    const recovering = ref(false);
    const peerReconnecting = ref(false);
    const mediaReconnecting = ref(false);
    const reconnecting = computed(() => media.reconnecting.value || recovering.value || peerReconnecting.value || mediaReconnecting.value);
    const duration = computed(() => {
        const minutes = Math.floor(elapsedSeconds.value / 60)
            .toString()
            .padStart(2, '0');
        const seconds = (elapsedSeconds.value % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    });

    let call = null;
    let pollTimer = null;
    let durationTimer = null;
    let disposed = false;
    let attempt = 0;
    let active = false;
    let cleanup = Promise.resolve();
    let peerTimer = null;
    let recoveryTimer = null;
    let recovery = null;
    let tokenRequest = null;
    let rtmEnabled = false;
    let expiryTimer = null;
    let ringTimer = null;
    const retryTimers = new Map();

    const isActive = current => active && !disposed && current === attempt;
    const isRetryable = error => error.retryable !== false &&
        ![401, 403].includes(error.status || error.response?.status) &&
        !['INVALID_TOKEN', 'INVALID_PARAMS', 'INVALID_APP_ID', 'INVALID_OPERATION',
            'UID_CONFLICT', 'CHANNEL_BANNED', 'IP_BANNED', 'UID_BANNED', 'SERVER_ERROR'].includes(error.code);

    function fail(code) {
        if (!active) return cleanup;
        errorCode.value = code;
        return finish(QR_CALL_STAGE.ERROR);
    }

    function clearRecoveryDeadline() {
        clearTimeout(recoveryTimer);
        recoveryTimer = null;
    }

    function startRecoveryDeadline(code) {
        if (recoveryTimer !== null) return;
        recoveryTimer = setTimeout(() => fail(code), CALL_RECOVERY_TIMEOUT_MS);
    }

    function waitForRetry() {
        return new Promise(resolve => {
            const timer = setTimeout(() => {
                retryTimers.delete(timer);
                resolve(true);
            }, CALL_RECOVERY_RETRY_MS);
            retryTimers.set(timer, resolve);
        });
    }

    function fetchToken(credentials, args, current) {
        if (!isActive(current)) return Promise.resolve(null);
        if (signaling.supportsTokenRenewal === false) return Promise.resolve(null);
        if (tokenRequest) return tokenRequest;
        const request = Promise.resolve().then(() => {
            if (!isActive(current)) return null;
            return signaling.renewToken({ callId: credentials.callId, ...args });
        })
            .then(token => {
                if (!isActive(current)) return null;
                if (!token) throw Object.assign(new Error('Token unavailable'), { retryable: false });
                return token;
            }).finally(() => {
                if (tokenRequest === request) tokenRequest = null;
            });
        tokenRequest = request;
        return request;
    }

    function recover(credentials, args, current, code) {
        if (!isActive(current)) return;
        if (signaling.supportsTokenRenewal === false &&
            (code === QR_CALL_ERROR.TOKEN_EXPIRED || credentials.expiresAtMillis <= Date.now())) {
            return fail(QR_CALL_ERROR.TOKEN_EXPIRED);
        }
        if (recovery) return recovery.promise;
        const operation = {};
        recovery = operation;
        recovering.value = true;
        mediaReconnecting.value = false;
        startRecoveryDeadline(code);
        operation.promise = (async () => {
            for (let index = 0; index < CALL_RECOVERY_ATTEMPTS; index++) {
                try {
                    const token = signaling.supportsTokenRenewal === false
                        ? credentials.token
                        : await fetchToken(credentials, args, current);
                    if (!isActive(current)) return;
                    await media.rejoin(token);
                    if (!isActive(current)) return;
                    recovering.value = false;
                    recovery = null;
                    clearRecoveryDeadline();
                    return;
                } catch (error) {
                    if (!isActive(current)) return;
                    if (!isRetryable(error) || index === CALL_RECOVERY_ATTEMPTS - 1) return fail(code);
                    if (!await waitForRetry() || !isActive(current)) return;
                }
            }
        })();
        return operation.promise;
    }

    function remoteJoined() {
        clearTimeout(peerTimer);
        peerTimer = null;
        peerReconnecting.value = false;
        markAnswered();
    }

    function remoteLeft(reason) {
        if (reason !== 'ServerTimeOut') return finish(QR_CALL_STAGE.ENDED);
        peerReconnecting.value = true;
        if (peerTimer !== null) return;
        peerTimer = setTimeout(() => fail(QR_CALL_ERROR.CONNECTION_LOST), REMOTE_RECONNECT_GRACE_MS);
    }

    function clearRingTimeout() {
        clearTimeout(ringTimer);
        ringTimer = null;
    }

    // The resident app may never reject or expire the call; stop ringing on our side and tell everyone
    function startRingTimeout(current) {
        clearRingTimeout();
        ringTimer = setTimeout(() => {
            ringTimer = null;
            if (!isActive(current) || ![QR_CALL_STAGE.CONNECTING, QR_CALL_STAGE.RINGING].includes(stage.value)) return;
            ringTimedOut.value = true;
            finish(QR_CALL_STAGE.MISSED, { publish: true });
        }, CALL_RING_TIMEOUT_MS);
    }

    const markAnswered = () => {
        if ([QR_CALL_STAGE.CONNECTING, QR_CALL_STAGE.RINGING].includes(stage.value)) {
            clearRingTimeout();
            stage.value = QR_CALL_STAGE.IN_CALL;
            const answeredAt = Date.now();
            durationTimer = setInterval(() => {
                elapsedSeconds.value = Math.floor((Date.now() - answeredAt) / 1000);
            }, 1000);
        }
    };

    function stopPolling() {
        clearTimeout(pollTimer);
        pollTimer = null;
    }

    function schedulePoll() {
        if (!call || !signaling.supportsStatusPolling) return;
        stopPolling();
        pollTimer = setTimeout(pollStatus, CALL_STATUS_POLL_INTERVAL_MS);
    }

    async function reconcileStatus() {
        if (!call || !signaling.supportsStatusPolling) return;
        stopPolling();
        const request = pollStatus();
        await new Promise(resolve => {
            const timer = setTimeout(() => {
                retryTimers.delete(timer);
                resolve();
            }, RTM_OPERATION_TIMEOUT_MS);
            retryTimers.set(timer, resolve);
            request.finally(() => {
                clearTimeout(timer);
                retryTimers.delete(timer);
                resolve();
            });
        });
    }

    async function pollStatus() {
        const current = call;
        if (!current) return;

        try {
            const { status, lockOpened: opened } = await signaling.getStatus(current.callId);
            if (call !== current) return;

            if (opened) lockOpened.value = true;

            if (QR_CALL_FINAL_STATUSES.includes(status)) {
                await finish(STAGE_BY_FINAL_STATUS[status], { notify: false });
                return;
            }

            if (status === QR_CALL_STATUS.ANSWERED) markAnswered();
        } catch {
            // Transient; the next poll retries
        }

        if (call === current) schedulePoll();
    }

    async function finish(nextStage, { notify = true, publish = false } = {}) {
        if (!active) return cleanup;
        active = false;
        attempt++;
        clearTimeout(expiryTimer);
        expiryTimer = null;
        clearRingTimeout();
        clearTimeout(peerTimer);
        peerTimer = null;
        clearRecoveryDeadline();
        retryTimers.forEach((resolve, timer) => {
            clearTimeout(timer);
            resolve(false);
        });
        retryTimers.clear();
        recovery = null;
        tokenRequest = null;
        recovering.value = false;
        peerReconnecting.value = false;
        mediaReconnecting.value = false;
        const current = call;
        call = null;
        stopPolling();
        clearInterval(durationTimer);
        durationTimer = null;
        stage.value = nextStage;

        const publication = publish && rtmEnabled ? rtm.sendCallEnded() : Promise.resolve();
        rtmEnabled = false;
        const rtmCleanup = Promise.resolve(publication).catch(() => {}).then(() => rtm.disconnect());
        const backendCleanup = notify && current ? signaling.hangup(current.callId) : Promise.resolve();
        cleanup = Promise.allSettled([media.leave(), rtmCleanup]).then(() => {});
        await Promise.allSettled([cleanup, backendCleanup]);
    }

    async function start(elements) {
        if (disposed || active) return;

        const current = ++attempt;
        active = true;
        const isCurrent = () => !disposed && active && current === attempt;

        stage.value = QR_CALL_STAGE.CONNECTING;
        endedByResident.value = false;
        ringTimedOut.value = false;
        errorCode.value = null;
        lockOpened.value = false;
        elapsedSeconds.value = 0;

        try {
            await cleanup;
            if (!isCurrent()) return;
            const allowed = await visitor.requestLocation();
            if (!isCurrent()) return;
            if (!allowed) {
                await fail(QR_CALL_ERROR.LOCATION_REQUIRED);
                return;
            }
            await media.prepareTracks();
        } catch (error) {
            if (!isCurrent()) return;
            errorCode.value = error.code || QR_CALL_ERROR.START_FAILED;
            await finish(QR_CALL_STAGE.ERROR);
            return;
        }

        if (!isCurrent()) return;

        try {
            const credentials = await signaling.startCall({
                entranceId,
                residentId,
                visitorSessionId: visitor.createCallSessionId(),
                coordinates: visitor.coordinates.value,
            });
            if (!isCurrent()) {
                await signaling.hangup(credentials.callId).catch(() => {});
                return;
            }
            call = credentials;
            if (Number.isFinite(credentials.expiresAtMillis)) {
                const scheduleExpiry = () => {
                    if (!isCurrent()) return;
                    const remaining = credentials.expiresAtMillis - Date.now();
                    if (remaining <= 0) return fail(QR_CALL_ERROR.TOKEN_EXPIRED);
                    expiryTimer = setTimeout(scheduleExpiry, Math.min(remaining, 2147483647));
                };
                scheduleExpiry();
                if (!isCurrent()) return;
            }
            startRingTimeout(current);
            media.configureMedia(credentials);
            schedulePoll();

            if (typeof getRtmConfig === 'function') {
                rtmEnabled = true;
                const connected = await rtm.connect(async () => {
                    const config = await getRtmConfig(credentials);
                    if (!isCurrent() || config?.callId !== credentials.callId) return null;
                    return config;
                }, {
                    onCommand: command => {
                        if (!isCurrent()) return;
                        if (command === QR_RTM_COMMAND.DIRECT_REJECT &&
                            [QR_CALL_STAGE.CONNECTING, QR_CALL_STAGE.RINGING].includes(stage.value)) {
                            return finish(QR_CALL_STAGE.DECLINED, { notify: false });
                        }
                        if (command === QR_RTM_COMMAND.CALL_EXPIRE &&
                            [QR_CALL_STAGE.CONNECTING, QR_CALL_STAGE.RINGING].includes(stage.value)) {
                            return finish(QR_CALL_STAGE.MISSED, { notify: false });
                        }
                        if (command === QR_RTM_COMMAND.CALL_ENDED) {
                            endedByResident.value = true;
                            return finish(QR_CALL_STAGE.ENDED, { notify: false });
                        }
                    },
                    onRestored: () => {
                        if (isCurrent()) return reconcileStatus();
                    },
                    onTokenExpired: () => isCurrent() && fail(QR_CALL_ERROR.TOKEN_EXPIRED),
                });
                if (!isCurrent()) return;
                rtmEnabled = connected;
                if (!connected && signaling.supportsTokenRenewal === false) throw new Error('RTM connection unavailable');
                if (connected) await reconcileStatus();
                if (!isCurrent()) return;
            } else {
                if (signaling.supportsTokenRenewal === false) throw new Error('RTM configuration unavailable');
            }

            await media.join(call, elements, {
                onRemoteJoined: () => isCurrent() && remoteJoined(),
                onRemoteLeft: reason => isCurrent() && remoteLeft(reason),
                onTokenWillExpire: args => fetchToken(credentials, args, current),
                onTokenExpired: args => recover(credentials, args, current, QR_CALL_ERROR.TOKEN_EXPIRED),
                onRenewalFailed: error => {
                    if (!isCurrent()) return false;
                    if (!isRetryable(error)) {
                        fail(QR_CALL_ERROR.TOKEN_EXPIRED);
                        return false;
                    }
                    return true;
                },
                onError: code => isCurrent() && fail(code),
                onConnectionStateChange: (state, reason, args = { channel: credentials.channel, uid: credentials.uid }) => {
                    if (!isCurrent()) return;
                    if (state === 'RECONNECTING') startRecoveryDeadline(QR_CALL_ERROR.CONNECTION_LOST);
                    if (state === 'CONNECTED' && !recovery && !mediaReconnecting.value) clearRecoveryDeadline();
                    if (state !== 'DISCONNECTED' || reason === 'LEAVE') return;
                    if (['NETWORK_ERROR', 'TOKEN_EXPIRE'].includes(reason)) {
                        return recover(credentials, args, current,
                            reason === 'TOKEN_EXPIRE' ? QR_CALL_ERROR.TOKEN_EXPIRED : QR_CALL_ERROR.CONNECTION_LOST);
                    }
                    return fail(QR_CALL_ERROR.CONNECTION_LOST);
                },
                onMediaReconnecting: value => {
                    if (!isCurrent()) return;
                    mediaReconnecting.value = value;
                    if (value) startRecoveryDeadline(QR_CALL_ERROR.CONNECTION_LOST);
                    else if (!recovery && !media.reconnecting.value) clearRecoveryDeadline();
                },
            });
            if (!isCurrent()) return;

            if (stage.value === QR_CALL_STAGE.CONNECTING) stage.value = QR_CALL_STAGE.RINGING;
        } catch (error) {
            if (!isCurrent()) return;
            if (isOutsideQrIntercomGeofence(error)) onOutsideGeofence?.(error);
            errorCode.value = QR_CALL_ERROR.START_FAILED;
            await finish(QR_CALL_STAGE.ERROR, { publish: true });
        }
    }

    const hangup = () => finish(QR_CALL_STAGE.ENDED, { publish: true });

    // Best effort: the request may not complete once the page is gone
    const onPageHide = () => {
        if (call || BUSY_STAGES.includes(stage.value)) hangup();
    };

    onMounted(() => window.addEventListener('pagehide', onPageHide));

    onBeforeUnmount(() => {
        disposed = true;
        clearInterval(durationTimer);
        clearRingTimeout();
        window.removeEventListener('pagehide', onPageHide);
        if (call || BUSY_STAGES.includes(stage.value)) hangup();
    });

    return {
        micOn: media.micOn,
        micAllowed: media.micAllowed,
        camOn: media.camOn,
        audioOnly: media.audioOnly,
        reconnecting,
        remoteVideoOn: media.remoteVideoOn,
        autoplayBlocked: media.autoplayBlocked,
        toggleMic: media.toggleMic,
        toggleCam: media.toggleCam,
        switchCamera: media.switchCamera,
        resumeMedia: media.resumeMedia,
        stage,
        endedByResident,
        ringTimedOut,
        errorCode,
        rtmConnected: rtm.connected,
        rtmError: rtm.errorCode,
        lockOpened,
        duration,
        start,
        hangup,
    };
}
